import React from 'react';
import { BaseballScore } from '../../types';

interface Props {
  score: BaseballScore;
  homeTeam: string;
  awayTeam: string;
  sport: 'baseball' | 'softball';
}

// Count dot indicator
const CountDots = ({ filled, total, color }: { filled: number; total: number; color: string }) => (
  <div style={{ display: 'flex', gap: '4px' }}>
    {Array.from({ length: total }, (_, i) => (
      <div key={i} style={{
        width: 10, height: 10, borderRadius: '50%',
        background: i < filled ? color : 'transparent',
        border: `2px solid ${i < filled ? color : 'var(--hairline-strong)'}`,
        transition: 'all 0.15s',
      }} />
    ))}
  </div>
);

const BaseballBoxScore: React.FC<Props> = ({ score, homeTeam, awayTeam, sport }) => {
  const totalInnings = Math.max(sport === 'softball' ? 7 : 9, score.current_inning);
  const innings = Array.from({ length: totalInnings }, (_, i) => i + 1);

  const getRuns = (team: 'home' | 'away', inning: number): string => {
    const found = score.inning_scores?.find(s => s.inning === inning);
    if (!found) return '-';
    return String(team === 'home' ? found.home : found.away);
  };

  const isBatting = (team: 'home' | 'away') =>
    team === 'away' ? score.is_top_inning === 1 : score.is_top_inning === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

      {/* Count bar */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px', padding: '14px 16px',
      }}>
        {/* Inning label */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px',
        }}>
          <div>
            <span style={{ fontSize: '13px', fontWeight: 800, color: '#F97316' }}>
              {score.is_top_inning ? '▲' : '▼'} {score.current_inning}
            </span>
            <span style={{ fontSize: '11px', color: 'var(--ink-3)', marginLeft: '6px' }}>
              {score.is_top_inning ? 'Top' : 'Bottom'} of {score.current_inning === 1 ? '1st' : score.current_inning === 2 ? '2nd' : score.current_inning === 3 ? '3rd' : `${score.current_inning}th`}
            </span>
          </div>
        </div>

        {/* Balls / Strikes / Outs */}
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink-4)', letterSpacing: '1px' }}>BALLS</div>
            <CountDots filled={score.balls} total={4} color="#22C55E" />
          </div>
          <div style={{ width: 1, height: 28, background: 'var(--hairline)' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink-4)', letterSpacing: '1px' }}>STRIKES</div>
            <CountDots filled={score.strikes} total={3} color="#F97316" />
          </div>
          <div style={{ width: 1, height: 28, background: 'var(--hairline)' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink-4)', letterSpacing: '1px' }}>OUTS</div>
            <CountDots filled={score.outs} total={3} color="#EF4444" />
          </div>

          {/* Bases diamond */}
          <div style={{ marginLeft: 'auto', position: 'relative', width: 60, height: 60, flexShrink: 0 }}>
            {[
              { key: 'second',  top: 0,    left: '50%', transform: 'translate(-50%, 0)', occupied: !!score.runner_second },
              { key: 'third',   top: '50%', left: 0,    transform: 'translate(0, -50%)', occupied: !!score.runner_third },
              { key: 'first',   top: '50%', right: 0,   transform: 'translate(0, -50%)', occupied: !!score.runner_first },
              { key: 'home',    bottom: 0, left: '50%', transform: 'translate(-50%, 0)', occupied: false, isHome: true },
            ].map(b => (
              <div key={b.key} style={{
                position: 'absolute',
                ...('top' in b && b.top !== undefined ? { top: b.top } : {}),
                ...('bottom' in b && b.bottom !== undefined ? { bottom: b.bottom } : {}),
                ...('left' in b && b.left !== undefined ? { left: b.left } : {}),
                ...('right' in b && b.right !== undefined ? { right: b.right } : {}),
                transform: `${b.transform} rotate(45deg)`,
                width: 14, height: 14,
                background: b.occupied ? '#F97316' : ('isHome' in b && b.isHome ? 'var(--surface-sunken)' : 'transparent'),
                border: `2px solid ${b.occupied ? '#F97316' : 'var(--hairline-strong)'}`,
                borderRadius: '2px',
                transition: 'all 0.2s',
              }} />
            ))}
          </div>
        </div>
      </div>

      {/* Line score table */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px',
        overflow: 'hidden',
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: 'var(--surface-sunken)' }}>
                <th style={{
                  padding: '9px 14px', textAlign: 'left', minWidth: '90px',
                  fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', letterSpacing: '0.5px',
                  borderBottom: '1px solid var(--hairline)',
                }}>
                  TEAM
                </th>
                {innings.map(n => (
                  <th key={n} style={{
                    padding: '9px 6px', textAlign: 'center', minWidth: '26px',
                    fontSize: '11px', fontWeight: 700,
                    color: n === score.current_inning ? '#F97316' : 'var(--ink-4)',
                    background: n === score.current_inning ? 'rgba(249,115,22,0.06)' : undefined,
                    borderBottom: `2px solid ${n === score.current_inning ? '#F97316' : 'var(--hairline)'}`,
                  }}>
                    {n}
                  </th>
                ))}
                {['R', 'H', 'E'].map((h, i) => (
                  <th key={h} style={{
                    padding: '9px 10px', textAlign: 'center',
                    fontSize: '11px', fontWeight: 800,
                    color: h === 'E' ? '#EF4444' : 'var(--ink-1)',
                    borderLeft: i === 0 ? '2px solid var(--hairline)' : undefined,
                    borderBottom: '1px solid var(--hairline)',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {([
                { team: 'away' as const, name: awayTeam, r: score.away_total_runs, h: score.away_hits, e: score.away_errors },
                { team: 'home' as const, name: homeTeam, r: score.home_total_runs, h: score.home_hits, e: score.home_errors },
              ]).map(row => (
                <tr key={row.team} style={{ borderTop: '1px solid var(--hairline)' }}>
                  <td style={{
                    padding: '11px 14px',
                    fontWeight: isBatting(row.team) ? 800 : 500,
                    color: isBatting(row.team) ? 'var(--ink-1)' : 'var(--ink-3)',
                    display: 'flex', alignItems: 'center', gap: '7px',
                    whiteSpace: 'nowrap',
                  }}>
                    {isBatting(row.team) && (
                      <span style={{ color: '#F97316', fontSize: '10px' }}>●</span>
                    )}
                    {row.name}
                  </td>
                  {innings.map(n => (
                    <td key={n} style={{
                      padding: '11px 6px', textAlign: 'center',
                      color: n === score.current_inning ? 'var(--ink-1)' : 'var(--ink-3)',
                      background: n === score.current_inning ? 'rgba(249,115,22,0.04)' : undefined,
                      fontSize: '13px',
                    }}>
                      {getRuns(row.team, n)}
                    </td>
                  ))}
                  <td style={{ padding: '11px 10px', textAlign: 'center', fontWeight: 900, fontSize: '16px', borderLeft: '2px solid var(--hairline)', color: 'var(--ink-1)' }}>
                    {row.r}
                  </td>
                  <td style={{ padding: '11px 10px', textAlign: 'center', color: 'var(--ink-3)' }}>{row.h}</td>
                  <td style={{ padding: '11px 10px', textAlign: 'center', color: '#EF4444', fontWeight: 600 }}>{row.e}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BaseballBoxScore;
